import * as React from 'react'
// libs
import { useQuery } from '@apollo/client/react'
import { WrappedFieldInputProps } from 'redux-form'
// graphql
import { GLOBAL_SEARCH_QUERY } from 'graphql/search/queries'
// constants
import { ENTITIES } from 'constants/index'
// types
import { IPlan, IPlanAction, IPlanKpi, IPlanObjective } from 'types/graphql'
// utils
import get from 'lodash/get'
import debounce from 'lodash/debounce'
import { getEntityPath } from 'utils/plan-tree'

type Props = React.PropsWithChildren<unknown>

const INITIAL_PER_PAGE = 10

interface IContext {
  clearSearch: () => void
  handleFilterChange: (event: React.ChangeEvent<HTMLInputElement>, input: WrappedFieldInputProps) => void
  handleSearch: (event: React.ChangeEvent<HTMLInputElement>) => void
  handleShowMore: () => void
  isLoading: boolean
  networkStatus: number
  searchResults: IPlan[] | IPlanObjective[] | IPlanAction[] | IPlanKpi[] | any
  shouldShowButton: boolean
}

export const GlobalSearchAPIContext = React.createContext<IContext>({} as any)

const GlobalSearchAPI: React.FC<Props> = ({ children }) => {
  // state
  const [moreResults, setMoreResults] = React.useState([])
  const [page, setPage] = React.useState<number>(1)
  const [search, setSearch] = React.useState<string>('')
  const [filters, setFilters] = React.useState<{ [key: string]: boolean }>({
    showActions: true,
    showKPIs: true,
    showPlans: true,
    showObjectives: true
  })

  const { data, fetchMore, loading, networkStatus } = useQuery(GLOBAL_SEARCH_QUERY, {
    fetchPolicy: 'network-only',
    skip: search.length < 3,
    notifyOnNetworkStatusChange: true,
    variables: {
      filter: {
        ...filters,
        search: search
      },
      pagination: {
        pageSkip: 0,
        pageTake: INITIAL_PER_PAGE
      }
    }
  })

  // methods
  const debouncedSearch = debounce((value) => {
    setSearch(value)
  }, 500)

  const clearSearch = () => {
    setSearch('')
    setPage(1)
  }

  const handleSearch = (event) => debouncedSearch(event.target.value)

  const handleFilterChange = (event, input) => setFilters({ ...filters, [input.name]: event.target.checked })

  const normalizeData = (data) => {
    const ENTITY_MAPPING = {
      plans: ENTITIES.PLAN,
      planKPIs: ENTITIES.PLAN_KPI,
      planActions: ENTITIES.PLAN_ACTION,
      planObjectives: ENTITIES.PLAN_OBJECTIVE
    }

    return Object.entries(ENTITY_MAPPING).flatMap(([key, type]) =>
      get(data, `globalSearch.${key}`, []).map((entity) => ({
        ...entity,
        type
      }))
    )
  }

  const generateResults = (data) => {
    const results = normalizeData(data)

    return [...results, ...moreResults].map((result) => {
      if (result.type !== ENTITIES.PLAN) {
        const path = getEntityPath(result)

        return { ...result, path }
      }
      return result
    })
  }

  const handleShowMore = () => {
    setPage(page + 1)

    fetchMore({
      variables: {
        filter: {
          ...filters,
          search: search
        },
        pagination: {
          pageSkip: INITIAL_PER_PAGE * page,
          pageTake: INITIAL_PER_PAGE
        }
      },
      updateQuery: (prev, { fetchMoreResult }) => {
        if (fetchMoreResult) {
          setMoreResults((state) => [...state, ...normalizeData(fetchMoreResult)])
        }
      }
    })
  }

  // constants
  const results = generateResults(data)
  const totalCount = get(data, 'globalSearch.pagination.totalCount', 0)
  const shouldShowButton = results.length !== 0 && results.length < totalCount

  React.useEffect(() => {
    if (page > 1) {
      setMoreResults([])
      setPage(1)
    }
  }, [search, filters])

  const value = {
    clearSearch,
    handleFilterChange,
    handleSearch,
    handleShowMore,
    isLoading: loading,
    networkStatus,
    searchResults: results,
    shouldShowButton
  }

  return <GlobalSearchAPIContext.Provider value={value}>{children}</GlobalSearchAPIContext.Provider>
}

export default GlobalSearchAPI
